import React from 'react'
import _castArray from 'lodash/castArray'
import _compact from 'lodash/compact'
import _filter from 'lodash/filter'
import _find from 'lodash/find'
import _get from 'lodash/get'
import _has from 'lodash/has'
import _includes from 'lodash/includes'
import _isEmpty from 'lodash/isEmpty'
import _isEqual from 'lodash/isEqual'
import _join from 'lodash/join'
import _map from 'lodash/map'
import _sortBy from 'lodash/sortBy'
import { BUNDLE_LABELS } from 'services/content/bundles'
import { CONTENT_ROOTS, getContentDetailHref } from 'services/content'
import {
  CONTENT_TYPE_SERIES,
  CONTENT_TYPE_VIDEO,
  getAllContentTypes,
} from 'services/content/content-types'
import {
  CONTENT_REFERENCE_TYPE_CATEGORY,
  CONTENT_REFERENCE_TYPE_CENTERS,
  CONTENT_REFERENCE_TYPE_CONTENT_ORIGIN,
  CONTENT_REFERENCE_TYPE_GENRES,
  CONTENT_REFERENCE_TYPE_INTERVIEW_GUEST,
  CONTENT_REFERENCE_TYPE_INTERVIEW_HOST,
  CONTENT_REFERENCE_TYPE_MARKETING_CENTERS,
  CONTENT_REFERENCE_TYPE_MEDITATION_DURATION,
  CONTENT_REFERENCE_TYPE_MEDITATION_FOCUS,
  CONTENT_REFERENCE_TYPE_MEDITATION_STYLE,
  CONTENT_REFERENCE_TYPE_MEDITATION_TEACHER,
  CONTENT_REFERENCE_TYPE_PRIMARY_COURSE,
  CONTENT_REFERENCE_TYPE_PRODUCT,
  CONTENT_REFERENCE_TYPE_RAINBOW_CATEGORY,
  CONTENT_REFERENCE_TYPE_SERIES,
  CONTENT_REFERENCE_TYPE_THIRD_PARTY_GENRE,
  CONTENT_REFERENCE_TYPE_YOGA_DURATION,
  CONTENT_REFERENCE_TYPE_YOGA_FOCUS,
  CONTENT_REFERENCE_TYPE_YOGA_LEVEL,
  CONTENT_REFERENCE_TYPE_YOGA_STYLE,
  CONTENT_REFERENCE_TYPE_YOGA_TEACHER,
} from 'services/content-references/types'
import {
  GridActionsCellItem,
  GridFilterInputMultipleSingleSelect,
  GridRowModes,
  useGridApiContext,
} from '@mui/x-data-grid-pro'
import Autocomplete from '@mui/material/Autocomplete'
import CancelIcon from '@mui/icons-material/Close'
import EditIcon from '@mui/icons-material/Edit'
import ExitToAppIcon from '@mui/icons-material/ExitToApp'
import SaveIcon from '@mui/icons-material/Save'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import {
  filterOperatorsDate,
  filterOperatorsNumeric,
  filterOperatorsSingleSelect,
  filterOperatorsString,
} from '@/services/mui-grid-pro'
import { ContentProviderProviders } from '@/__generated__/gql/graphql'

export const COURSE_FILTER_FIELD = 'course'

const CONTENT_PROVIDER_OPTIONS = [
  { value: '', label: 'Any' },
  { value: ContentProviderProviders.YogaInternational, label: 'Yoga International' },
  { value: ContentProviderProviders.GaiaBonus, label: 'Gaia Bonus' },
  { value: ContentProviderProviders.GaiaWordpress, label: 'Gaia Wordpress' },
  { value: ContentProviderProviders.Shopify, label: 'Shopify' },
]

function ContentReferenceSelect (props) {
  const { id, value: valueProp, field, multiple = false, optionsData = [] } = props
  const apiRef = useGridApiContext()
  const handleChange = async (event, item) => {
    const isValid = await apiRef.current.setEditCellValue({ id, field, value: item })
    if (isValid) {
      apiRef.current.stopCellEditMode({ id, field })
    }
  }

  return (
    <Autocomplete
      fullWidth
      value={valueProp}
      multiple={multiple}
      options={optionsData}
      getOptionLabel={({ title = '' }) => title}
      filterSelectedOptions
      onChange={handleChange}
      getOptionKey={({ titleGroupId }) => titleGroupId}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={_isEmpty(valueProp) ? 'Choose an option' : ''}
          InputProps={{
            ...params.InputProps,
            endAdornment: <>{params.InputProps.endAdornment}</>,
          }}
        />
      )}
      isOptionEqualToValue={(o, v) =>
        !_isEmpty(v) && _has(v, 'titleGroupId')
          ? o.titleGroupId === v.titleGroupId
          : o.titleGroupId === v
      }
      freeSolo
      forcePopupIcon={true}
    />
  )
}

export function contentReferenceSingleSelectValueParameters (type, optionsData) {
  return {
    valueGetter: ({ row }) =>
      _get(
        _filter(_get(row, 'contentReferencesFrom', []), { contentReferenceType: { type } }),
        [0, 'contentTo', 0],
        '',
      ),
    valueSetter: ({ row, value }) => {
      const { contentReferencesFrom: existingContentReferencesFrom = [] } = row
      const existingSameTypeContentReferences = _filter(existingContentReferencesFrom, {
        contentReferenceType: { type },
      })
      const valueHasChanged = !_isEqual(
        value,
        _get(existingSameTypeContentReferences, [0, 'contentTo', 0]),
      )
      const contentReferencesFrom = valueHasChanged
        ? _compact([
            ..._filter(
              existingContentReferencesFrom,
              (i) => !_includes(_map(existingSameTypeContentReferences, 'id'), _get(i, 'id')),
            ),
            _has(value, 'titleGroupId') && _has(value, 'title')
              ? {
                  contentReferenceType: { type },
                  contentTo: [{ titleGroupId: value.titleGroupId }],
                }
              : null,
          ])
        : existingContentReferencesFrom
      return { ...row, contentReferencesFrom }
    },
    valueFormatter: ({ value }) =>
      _isEmpty(value)
        ? ''
        : _get(
            _find(optionsData, { titleGroupId: value?.titleGroupId }),
            'title',
            `${value?.title}`,
          ),
    getOptionLabel: (o) => _get(o, 'title', ''),
    getOptionValue: (o) => _get(o, 'titleGroupId', ''),
    valueOptions:
      _map(optionsData, ({ title, titleGroupId }) => ({
        key: `${type}-${titleGroupId}`,
        title,
        titleGroupId,
      })) || [],
    renderEditCell: (params) => <ContentReferenceSelect {...params} optionsData={optionsData} />,
  }
}

export function contentReferenceMultipleSelectValueParameters (type, optionsData) {
  return {
    valueGetter: ({ row }) =>
      _sortBy(
        _map(
          _filter(_get(row, 'contentReferencesFrom', []), { contentReferenceType: { type } }),
          (r) => _get(r, ['contentTo', 0]),
        ),
        ['title'],
      ),
    valueSetter: ({ row, value }) => {
      const { contentReferencesFrom: existingContentReferencesFrom = [] } = row
      const existingSameTypeContentReferences = _filter(existingContentReferencesFrom, {
        contentReferenceType: { type },
      })
      const valueHasChanged = !_isEqual(
        value,
        _sortBy(
          _map(existingSameTypeContentReferences, (r) => _get(r, ['contentTo', 0])),
          ['title'],
        ),
      )
      const contentReferencesFrom = valueHasChanged
        ? _compact([
            ..._filter(
              existingContentReferencesFrom,
              (i) => !_includes(_map(existingSameTypeContentReferences, 'id'), _get(i, 'id')),
            ),
            ..._map(value, (v) =>
              _has(v, 'titleGroupId') && _has(v, 'title')
                ? {
                    contentReferenceType: { type },
                    contentTo: [v],
                  }
                : null,
            ),
          ])
        : existingContentReferencesFrom
      return { ...row, contentReferencesFrom }
    },
    valueFormatter: ({ value }) => {
      if (_isEmpty(value)) return ''
      const titleArray = _map(_castArray(value), (v) =>
        _get(_find(optionsData, { titleGroupId: v?.titleGroupId }), 'title', `${v?.title}`),
      )
      return _join(titleArray, ', ')
    },
    getOptionLabel: (o) => _get(o, 'title', ''),
    getOptionValue: (o) => _get(o, 'titleGroupId', ''),
    valueOptions:
      _map(optionsData, ({ title, titleGroupId }) => ({
        key: `${type}-${titleGroupId}`,
        title,
        titleGroupId,
      })) || [],
    renderEditCell: (params) => (
      <ContentReferenceSelect {...params} multiple={true} optionsData={optionsData} />
    ),
  }
}

const multipleSelectFilterOperators = [
  {
    value: 'contains',
    InputComponent: GridFilterInputMultipleSingleSelect,
  },
]

/**
 * Build the raw column definitions for the content list grid.
 * Caller is responsible for applying createPrioritySortedColumns(columnOrder, columns).
 *
 * @param {Object} options
 * @param {Object} options.rowModesModel
 * @param {Function} options.setRowModesModel
 * @param {Function} options.displaySnackbar
 * @param {Array} options.seriesOptions
 * @param {Array} options.rainbowCategoryOptions
 * @param {Array} options.centersOptions
 * @param {Array} options.productOptions
 * @param {Array} options.genreOptions
 * @param {Array} options.thirdPartyGenreOptions
 * @param {Array} options.contentOriginOptions
 * @param {Array} options.interviewHostOptions
 * @param {Array} options.interviewGuestOptions
 * @param {Array} options.categoryOptions
 * @param {Array} options.marketingCentersOptions
 * @param {Array} options.meditationFocusOptions
 * @param {Array} options.meditationStyleOptions
 * @param {Array} options.meditationTeacherOptions
 * @param {Array} options.meditationDurationOptions
 * @param {Array} options.yogaDurationOptions
 * @param {Array} options.yogaFocusOptions
 * @param {Array} options.yogaLevelOptions
 * @param {Array} options.yogaStyleOptions
 * @param {Array} options.yogaTeacherOptions
 * @param {Array} options.primaryCourseOptions
 * @returns {Array} Column definitions for MUI DataGridPro
 */
export function buildColumns (options) {
  const {
    rowModesModel,
    setRowModesModel,
    displaySnackbar,
    seriesOptions = [],
    rainbowCategoryOptions = [],
    centersOptions = [],
    productOptions = [],
    genreOptions = [],
    thirdPartyGenreOptions = [],
    contentOriginOptions = [],
    interviewHostOptions = [],
    interviewGuestOptions = [],
    categoryOptions = [],
    marketingCentersOptions = [],
    meditationFocusOptions = [],
    meditationStyleOptions = [],
    meditationTeacherOptions = [],
    meditationDurationOptions = [],
    yogaDurationOptions = [],
    yogaFocusOptions = [],
    yogaLevelOptions = [],
    yogaStyleOptions = [],
    yogaTeacherOptions = [],
    primaryCourseOptions = [],
  } = options

  const handleEditClick = (id) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } })
  }
  const handleSaveClick = (id) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } })
  }
  const handleCancelClick = (id) => () => {
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.View, ignoreModifications: true },
    })
    displaySnackbar('Edit Canceled. No changes made')
  }

  return [
    {
      field: 'actions',
      type: 'actions',
      width: 120,
      getActions: ({ row }) => {
        const { contentType, id, previewMediaId, featureMediaId } = row
        const provider = _get(row, 'contentProvider.provider', '')
        const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit
        const isSeries = contentType === CONTENT_TYPE_SERIES
        const isVideo = contentType === CONTENT_TYPE_VIDEO
        const detailLink = getContentDetailHref(id, contentType, { provider })
        const legacyLink = `${CONTENT_ROOTS.BASE}/${id}`
        return _compact([
          isInEditMode ? (
            <Tooltip title="Save" key="save">
              <GridActionsCellItem
                icon={<SaveIcon />}
                label="Save"
                sx={{ color: 'primary.main' }}
                onClick={handleSaveClick(id)}
              />
            </Tooltip>
          ) : null,
          isInEditMode ? (
            <Tooltip title="Cancel" key="cancel">
              <GridActionsCellItem
                icon={<CancelIcon />}
                label="Cancel"
                className="textPrimary"
                onClick={handleCancelClick(id)}
                color="inherit"
              />
            </Tooltip>
          ) : null,
          !isInEditMode ? (
            <Tooltip title="Edit" key="edit">
              <GridActionsCellItem
                icon={<EditIcon />}
                label="Edit"
                className="textPrimary"
                onClick={handleEditClick(id)}
                color="inherit"
              />
            </Tooltip>
          ) : null,
          <Tooltip title="Details" key="open-detail-page">
            <GridActionsCellItem
              icon={<ExitToAppIcon />}
              label="Open Detail Page"
              className="textPrimary"
              onClick={() => window.open(detailLink, '_blank')}
              color="inherit"
            />
          </Tooltip>,
          !isInEditMode ? (
            <GridActionsCellItem
              icon={<EditIcon />}
              key={`edit-${id}`}
              label="v1 Edit page"
              onClick={() => window.open(legacyLink, '_blank')}
              showInMenu
            />
          ) : null,
          !isInEditMode && (isSeries || isVideo) ? (
            <GridActionsCellItem
              icon={<EditIcon />}
              key={`edit-preview-${id}`}
              label={`Preview media${!previewMediaId ? ' not assigned' : ''}`}
              onClick={() => window.open(`/media/${previewMediaId}`, '_blank')}
              showInMenu
              disabled={!previewMediaId}
            />
          ) : null,
          !isInEditMode && isVideo ? (
            <GridActionsCellItem
              icon={<EditIcon />}
              key={`edit-feature-${id}`}
              label={`Feature media${!featureMediaId ? ' not assigned' : ''}`}
              onClick={() => window.open(`/media/${featureMediaId}`, '_blank')}
              showInMenu
              disabled={!featureMediaId}
            />
          ) : null,
        ])
      },
    },
    {
      field: 'contentId',
      headerName: 'Content ID',
      type: 'number',
      width: 85,
      valueFormatter: ({ value }) => value,
      filterOperators: filterOperatorsNumeric,
    },
    {
      field: 'contentType',
      headerName: 'Content Type',
      type: 'singleSelect',
      valueOptions: getAllContentTypes(),
      sortable: false,
      filterOperators: filterOperatorsSingleSelect,
    },
    {
      field: 'title',
      headerName: 'Title/Name',
      filterOperators: filterOperatorsString,
      editable: true,
      filterable: true,
      width: 350,
    },
    {
      field: CONTENT_REFERENCE_TYPE_SERIES,
      headerName: 'Series',
      width: 250,
      type: 'singleSelect',
      ...contentReferenceSingleSelectValueParameters(CONTENT_REFERENCE_TYPE_SERIES, seriesOptions),
      filterable: true,
      filterOperators: filterOperatorsSingleSelect,
      sortable: false,
      editable: true,
    },
    {
      field: CONTENT_REFERENCE_TYPE_PRIMARY_COURSE,
      headerName: 'Primary Course',
      width: 250,
      type: 'singleSelect',
      ...contentReferenceSingleSelectValueParameters(
        CONTENT_REFERENCE_TYPE_PRIMARY_COURSE,
        primaryCourseOptions,
      ),
      filterable: true,
      filterOperators: filterOperatorsSingleSelect,
      sortable: false,
      editable: true,
    },
    {
      field: COURSE_FILTER_FIELD,
      headerName: 'Course',
      width: 250,
      type: 'singleSelect',
      valueGetter: ({ row }) => {
        const courseModuleRefs = _get(row, 'courseModuleRefs', [])
        const course = _get(
          _compact(
            _map(courseModuleRefs, (ref) =>
              _get(ref, ['contentTo', 0, 'courseRef', 0, 'contentTo', 0]),
            ),
          ),
          [0],
          '',
        )
        return course
      },
      valueFormatter: ({ value }) => (_isEmpty(value) ? '' : _get(value, 'title', '')),
      getOptionLabel: (o) => _get(o, 'title', ''),
      getOptionValue: (o) => _get(o, 'titleGroupId', ''),
      valueOptions: _map(primaryCourseOptions, (c) => ({
        key: `course-${c.titleGroupId}`,
        title: c.title,
        titleGroupId: c.titleGroupId,
      })),
      filterable: true,
      filterOperators: filterOperatorsSingleSelect,
      sortable: false,
      editable: false,
    },
    {
      field: 'published',
      headerName: 'Published',
      type: 'boolean',
      width: 80,
      sortable: false,
      filterable: filterOperatorsSingleSelect,
      editable: true,
    },
    {
      field: 'publishedAtDate',
      headerName: 'Air Date',
      type: 'dateTime',
      valueGetter: ({ value }) => new Date(value),
      valueSetter: ({ row, value }) => ({ ...row, publishedAtDate: value.toISOString() }),
      valueFormatter: ({ value }) => {
        const formattedDate = value.toLocaleDateString('en-US', {
          year: '2-digit',
          month: '2-digit',
          day: '2-digit',
          hour: 'numeric',
          minute: 'numeric',
        })
        return formattedDate
      },
      editable: true,
      sortable: true,
      filterable: true,
      filterOperators: filterOperatorsDate,
      width: 150,
    },
    {
      field: 'contentProviderProvider',
      headerName: 'Content Provider',
      width: 180,
      type: 'singleSelect',
      valueGetter: ({ row }) => _get(row, 'contentProvider.provider', ''),
      valueOptions: CONTENT_PROVIDER_OPTIONS,
      filterable: true,
      filterOperators: [
        {
          label: 'is',
          value: 'equals',
          getApplyFilterFn: (filterItem) => {
            if (!filterItem.value) return null
            return (params) => _get(params.row, 'contentProvider.provider', '') === filterItem.value
          },
          InputComponent: GridFilterInputMultipleSingleSelect,
        },
        {
          label: 'is not',
          value: 'notEquals',
          getApplyFilterFn: (filterItem) => {
            if (!filterItem.value) return null
            return (params) => _get(params.row, 'contentProvider.provider', '') !== filterItem.value
          },
          InputComponent: GridFilterInputMultipleSingleSelect,
        },
      ],
      editable: false,
      sortable: true,
    },
    {
      field: 'path',
      headerName: 'Path',
      filterOperators: filterOperatorsString,
      editable: false,
      width: 350,
    },
    {
      field: 'seasonNumber',
      headerName: 'Season',
      type: 'number',
      width: 80,
      filterable: false,
      editable: true,
    },
    {
      field: 'episodeNumber',
      headerName: 'Episode',
      type: 'number',
      width: 80,
      filterable: false,
      editable: true,
    },
    {
      field: CONTENT_REFERENCE_TYPE_RAINBOW_CATEGORY,
      headerName: 'Rainbow Category',
      width: 160,
      type: 'singleSelect',
      ...contentReferenceSingleSelectValueParameters(
        CONTENT_REFERENCE_TYPE_RAINBOW_CATEGORY,
        rainbowCategoryOptions,
      ),
      filterable: true,
      filterOperators: filterOperatorsSingleSelect,
      sortable: false,
      editable: true,
    },
    {
      field: CONTENT_REFERENCE_TYPE_CENTERS,
      headerName: 'Centers',
      width: 350,
      type: 'singleSelect',
      ...contentReferenceMultipleSelectValueParameters(
        CONTENT_REFERENCE_TYPE_CENTERS,
        centersOptions,
      ),
      filterable: true,
      sortable: false,
      editable: true,
      filterOperators: multipleSelectFilterOperators,
    },
    {
      field: 'contentPolicyPolicyId',
      headerName: 'Content Policy ID',
      type: 'number',
      width: 130,
      valueFormatter: ({ value }) => value,
      filterable: true,
      editable: true,
      filterOperators: filterOperatorsNumeric,
      sortable: false,
    },
    {
      field: 'id',
      headerName: 'ID',
      type: 'number',
      valueFormatter: ({ value }) => value,
      filterOperators: filterOperatorsNumeric,
    },
    {
      field: 'titleGroupId',
      headerName: 'Title Group ID',
      width: 130,
      type: 'string',
      filterable: false,
      sortable: true,
    },
    {
      field: 'titleId',
      headerName: 'Title ID',
      width: 130,
      type: 'string',
      filterable: false,
      sortable: true,
    },
    {
      field: 'language',
      headerName: 'Language',
      filterable: false,
      sortable: false,
    },
    {
      field: 'unpublishedAt',
      headerName: 'Unpublished At',
      type: 'dateTime',
      valueGetter: ({ value }) => new Date(value),
      valueSetter: ({ row, value }) => ({ ...row, publishedAtDate: value.toISOString() }),
      sortable: false,
      filterable: false,
      width: 180,
    },
    {
      field: CONTENT_REFERENCE_TYPE_PRODUCT,
      headerName: 'Product',
      width: 250,
      type: 'singleSelect',
      ...contentReferenceSingleSelectValueParameters(
        CONTENT_REFERENCE_TYPE_PRODUCT,
        productOptions,
      ),
      filterable: true,
      filterOperators: filterOperatorsSingleSelect,
      sortable: false,
      editable: true,
    },
    {
      field: 'previewMediaId',
      headerName: 'Preview Media ID',
      type: 'number',
      width: 130,
      valueFormatter: ({ value }) => value,
      filterable: false,
      sortable: false,
    },
    {
      field: 'featureMediaId',
      headerName: 'Feature Media ID',
      type: 'number',
      width: 130,
      valueFormatter: ({ value }) => value,
      filterable: false,
      sortable: false,
    },
    {
      field: 'primaryLanguage',
      headerName: 'Primary Language',
      width: 130,
      filterable: false,
      sortable: false,
    },
    {
      field: 'contentSearchNoIndex',
      headerName: 'Include in Search',
      type: 'boolean',
      width: 130,
      filterable: false,
      sortable: false,
      editable: true,
      valueGetter: ({ value }) => !value,
      valueSetter: ({ row, value }) => ({ ...row, contentSearchNoIndex: !value }),
    },
    {
      field: 'excludeFromRecommendations',
      headerName: 'Include in Recommendations',
      type: 'boolean',
      width: 130,
      filterable: false,
      sortable: false,
      editable: true,
      valueGetter: ({ value }) => !value,
      valueSetter: ({ row, value }) => ({ ...row, excludeFromRecommendations: !value }),
    },
    {
      field: 'filterType',
      headerName: 'Filter Type(s)',
      width: 200,
      filterable: false,
    },
    {
      field: 'bundle',
      headerName: 'Bundle',
      width: 130,
      type: 'singleSelect',
      valueOptions: BUNDLE_LABELS.keySeq().toArray(),
      filterOperators: filterOperatorsSingleSelect,
      sortable: true,
    },
    {
      field: 'displayType',
      headerName: 'Display Type',
      width: 130,
      filterable: false,
    },
    {
      field: 'productType',
      headerName: 'Product Type',
      width: 130,
      filterable: false,
    },
    {
      field: 'weight',
      headerName: 'Weight',
      type: 'number',
      width: 130,
      filterable: false,
    },
    {
      field: 'createdAt',
      headerName: 'Created At',
      type: 'dateTime',
      valueGetter: ({ value }) => new Date(value),
      valueSetter: ({ row, value }) => ({ ...row, publishedAtDate: value.toISOString() }),
      sortable: true,
      filterable: false,
      width: 180,
    },
    {
      field: 'description',
      headerName: 'Description',
      type: 'string',
      width: 350,
      filterable: false,
      editable: false,
      sortable: false,
    },
    {
      field: 'updatedAt',
      headerName: 'Updated At',
      type: 'dateTime',
      valueGetter: ({ value }) => new Date(value),
      valueSetter: ({ row, value }) => ({ ...row, publishedAtDate: value.toISOString() }),
      sortable: true,
      filterable: false,
      width: 180,
    },
    {
      field: CONTENT_REFERENCE_TYPE_GENRES,
      headerName: 'Genre',
      width: 150,
      type: 'singleSelect',
      ...contentReferenceSingleSelectValueParameters(CONTENT_REFERENCE_TYPE_GENRES, genreOptions),
      filterable: true,
      filterOperators: filterOperatorsSingleSelect,
      sortable: false,
      editable: true,
    },
    {
      field: CONTENT_REFERENCE_TYPE_THIRD_PARTY_GENRE,
      headerName: 'Third Party Genre',
      width: 160,
      type: 'singleSelect',
      ...contentReferenceSingleSelectValueParameters(
        CONTENT_REFERENCE_TYPE_THIRD_PARTY_GENRE,
        thirdPartyGenreOptions,
      ),
      filterable: true,
      filterOperators: filterOperatorsSingleSelect,
      sortable: false,
      editable: true,
    },
    {
      field: CONTENT_REFERENCE_TYPE_CONTENT_ORIGIN,
      headerName: 'Content Origin',
      width: 250,
      type: 'singleSelect',
      ...contentReferenceSingleSelectValueParameters(
        CONTENT_REFERENCE_TYPE_CONTENT_ORIGIN,
        contentOriginOptions,
      ),
      filterable: true,
      filterOperators: filterOperatorsSingleSelect,
      sortable: false,
      editable: true,
    },
    {
      field: 'studio',
      headerName: 'Studio',
      width: 130,
      filterable: false,
      editable: true,
      sortable: true,
    },
    {
      field: 'copyright',
      headerName: 'Copyright',
      width: 130,
      filterable: false,
      editable: true,
      sortable: true,
    },
    {
      field: 'instructor',
      headerName: 'Instructor',
      width: 200,
      filterable: false,
      editable: true,
      sortable: true,
    },
    {
      field: 'cast',
      headerName: 'Cast',
      width: 200,
      filterable: false,
      editable: true,
      sortable: true,
    },
    {
      field: CONTENT_REFERENCE_TYPE_INTERVIEW_HOST,
      headerName: 'Interview Host',
      width: 350,
      type: 'singleSelect',
      ...contentReferenceMultipleSelectValueParameters(
        CONTENT_REFERENCE_TYPE_INTERVIEW_HOST,
        interviewHostOptions,
      ),
      filterable: true,
      sortable: false,
      editable: true,
      filterOperators: multipleSelectFilterOperators,
    },
    {
      field: CONTENT_REFERENCE_TYPE_INTERVIEW_GUEST,
      headerName: 'Interview Guest',
      width: 350,
      type: 'singleSelect',
      ...contentReferenceMultipleSelectValueParameters(
        CONTENT_REFERENCE_TYPE_INTERVIEW_GUEST,
        interviewGuestOptions,
      ),
      filterable: true,
      sortable: false,
      editable: true,
      filterOperators: multipleSelectFilterOperators,
    },
    {
      field: CONTENT_REFERENCE_TYPE_CATEGORY,
      headerName: 'Category',
      width: 350,
      type: 'singleSelect',
      ...contentReferenceMultipleSelectValueParameters(
        CONTENT_REFERENCE_TYPE_CATEGORY,
        categoryOptions,
      ),
      filterable: true,
      sortable: false,
      editable: true,
      filterOperators: multipleSelectFilterOperators,
    },
    {
      field: CONTENT_REFERENCE_TYPE_MARKETING_CENTERS,
      headerName: 'Marketing Centers',
      width: 350,
      type: 'singleSelect',
      ...contentReferenceMultipleSelectValueParameters(
        CONTENT_REFERENCE_TYPE_MARKETING_CENTERS,
        marketingCentersOptions,
      ),
      filterable: true,
      sortable: false,
      editable: true,
      filterOperators: multipleSelectFilterOperators,
    },
    {
      field: CONTENT_REFERENCE_TYPE_MEDITATION_FOCUS,
      headerName: 'Meditation Focus',
      width: 350,
      type: 'singleSelect',
      ...contentReferenceMultipleSelectValueParameters(
        CONTENT_REFERENCE_TYPE_MEDITATION_FOCUS,
        meditationFocusOptions,
      ),
      filterable: true,
      sortable: false,
      editable: true,
      filterOperators: multipleSelectFilterOperators,
    },
    {
      field: CONTENT_REFERENCE_TYPE_MEDITATION_STYLE,
      headerName: 'Meditation Style',
      width: 350,
      type: 'singleSelect',
      ...contentReferenceMultipleSelectValueParameters(
        CONTENT_REFERENCE_TYPE_MEDITATION_STYLE,
        meditationStyleOptions,
      ),
      filterable: true,
      sortable: false,
      editable: true,
      filterOperators: multipleSelectFilterOperators,
    },
    {
      field: CONTENT_REFERENCE_TYPE_MEDITATION_TEACHER,
      headerName: 'Meditation Teacher',
      width: 350,
      type: 'singleSelect',
      ...contentReferenceMultipleSelectValueParameters(
        CONTENT_REFERENCE_TYPE_MEDITATION_TEACHER,
        meditationTeacherOptions,
      ),
      filterable: true,
      sortable: false,
      editable: true,
      filterOperators: multipleSelectFilterOperators,
    },
    {
      field: CONTENT_REFERENCE_TYPE_MEDITATION_DURATION,
      headerName: 'Meditation Duration',
      width: 350,
      type: 'singleSelect',
      ...contentReferenceMultipleSelectValueParameters(
        CONTENT_REFERENCE_TYPE_MEDITATION_DURATION,
        meditationDurationOptions,
      ),
      filterable: true,
      sortable: false,
      editable: true,
      filterOperators: multipleSelectFilterOperators,
    },
    {
      field: CONTENT_REFERENCE_TYPE_YOGA_DURATION,
      headerName: 'Yoga Duration',
      width: 350,
      type: 'singleSelect',
      ...contentReferenceMultipleSelectValueParameters(
        CONTENT_REFERENCE_TYPE_YOGA_DURATION,
        yogaDurationOptions,
      ),
      filterable: true,
      sortable: false,
      editable: true,
      filterOperators: multipleSelectFilterOperators,
    },
    {
      field: CONTENT_REFERENCE_TYPE_YOGA_FOCUS,
      headerName: 'Yoga Focus',
      width: 350,
      type: 'singleSelect',
      ...contentReferenceMultipleSelectValueParameters(
        CONTENT_REFERENCE_TYPE_YOGA_FOCUS,
        yogaFocusOptions,
      ),
      filterable: true,
      sortable: false,
      editable: true,
      filterOperators: multipleSelectFilterOperators,
    },
    {
      field: CONTENT_REFERENCE_TYPE_YOGA_LEVEL,
      headerName: 'Yoga Level',
      width: 350,
      type: 'singleSelect',
      ...contentReferenceMultipleSelectValueParameters(
        CONTENT_REFERENCE_TYPE_YOGA_LEVEL,
        yogaLevelOptions,
      ),
      filterable: true,
      sortable: false,
      editable: true,
      filterOperators: multipleSelectFilterOperators,
    },
    {
      field: CONTENT_REFERENCE_TYPE_YOGA_STYLE,
      headerName: 'Yoga Style',
      width: 350,
      type: 'singleSelect',
      ...contentReferenceMultipleSelectValueParameters(
        CONTENT_REFERENCE_TYPE_YOGA_STYLE,
        yogaStyleOptions,
      ),
      filterable: true,
      sortable: false,
      editable: true,
      filterOperators: multipleSelectFilterOperators,
    },
    {
      field: CONTENT_REFERENCE_TYPE_YOGA_TEACHER,
      headerName: 'Yoga Teacher',
      width: 350,
      type: 'singleSelect',
      ...contentReferenceMultipleSelectValueParameters(
        CONTENT_REFERENCE_TYPE_YOGA_TEACHER,
        yogaTeacherOptions,
      ),
      filterable: true,
      sortable: false,
      editable: true,
      filterOperators: multipleSelectFilterOperators,
    },
    {
      field: 'contentAssetReferences',
      headerName: 'PDF Assets',
      width: 300,
      type: 'string',
      valueGetter: ({ row }) => {
        const assetRefs = _get(row, 'contentAssetReferences', [])
        if (_isEmpty(assetRefs)) return ''

        const pdfAssetRefs = _filter(assetRefs, (ref) => {
          const mimeType = _get(ref, 'asset.mimeType', '')
          return mimeType === 'application/pdf'
        })

        if (_isEmpty(pdfAssetRefs)) return ''

        return _join(
          _map(pdfAssetRefs, (ref) => {
            const fileName = _get(ref, 'asset.originalFileName', 'unnamed')
            return `${fileName}`
          }),
          ', ',
        )
      },
      valueFormatter: ({ value }) => value,
      filterable: false,
      sortable: false,
      editable: false,
    },
  ]
}
